import { type PropsWithChildren, Suspense, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { PageHeader, useGetFullRelease, useProject } from "@/common";
import { useResourceInstance } from "@/legacy/redux";

import { AppScope } from "@/common/AppScope";
import { isFeatureEnabled } from "@/config/features";
import { ReleaseChangeHistoryButton } from "@/features/release";
import { RELEASE_LAYER } from "@/ui/dialogs/layers";
import Chip from "@imminently/decisively-ui/Chip";
import Icon from "@imminently/decisively-ui/Icon";
import LoadingOverlay from "@imminently/decisively-ui/LoadingOverlay";
import { PersistentComponents } from "@imminently/decisively-ui/PersistentComponents";
import { NAMESPACE_OPT_IN } from "@packages/commons";
import { useScope } from "../../../redux/scope";
import GraphContext, { ParsedGraphContextProvider } from "./GraphContext";
import ReleaseContextProvider from "./ReleaseContextProvider";

export type ReleaseWrapperProps = {
  title: string;
  header?: boolean;
  scopeComponent?: any;
} & PropsWithChildren;

const ReleaseWrapper = ({ children, title, header = true, scopeComponent }: ReleaseWrapperProps) => {
  useGetFullRelease();
  const dispatch = useDispatch();
  const { project: projectId, release: releaseId } = useScope();
  const project = useProject(projectId);
  // const release = useRelease(releaseId);
  const { record: release } = useResourceInstance("releases", releaseId);
  const [graph, setGraph] = useState();

  // deprecated, this nolonger appears to be needed, as we are using getFullRelease, and dispatching crudGetOne elsewhere
  // assuming nothing is broken, this can be removed
  // useEffect(() => {
  //   if (releaseId) dispatch(crudGetOne("releases", releaseId));
  // }, [dispatch, releaseId]);

  useEffect(() => {
    if (release?.rule_graph) setGraph(release.rule_graph);
  }, [dispatch, release]);

  const namespacesEnabled = Boolean(project?.name?.includes(NAMESPACE_OPT_IN));
  const isCompact = isFeatureEnabled("design.compact");

  // const validRelease = release && (graph || pathname === "/build/entities") && release.model === projectId && (project ? project.workspace === workspace : true);
  return (
    <ReleaseContextProvider release={release}>
      <ParsedGraphContextProvider>
        <GraphContext.Provider value={graph}>
          <PersistentComponents layer={RELEASE_LAYER} />
          <div className="flex h-full min-h-0 flex-col overflow-hidden">
            <div className="shrink-0">
              {header &&
                (isCompact ? (
                  <AppScope />
                ) : (
                  <PageHeader
                    title={title}
                    scope
                    scopeComponent={scopeComponent}
                  >
                    <div className="flex items-center gap-4">
                      {namespacesEnabled ? (
                        <Chip
                          variant="warning"
                          icon={<Icon name="info" />}
                          label="Namespaces enabled for project"
                        />
                      ) : null}
                      <ReleaseChangeHistoryButton />
                    </div>
                  </PageHeader>
                ))}
            </div>
            <div className="relative flex min-h-0 w-full flex-1 flex-col">
              <Suspense fallback={<LoadingOverlay message="Loading..." />}>{children}</Suspense>
            </div>
          </div>
        </GraphContext.Provider>
      </ParsedGraphContextProvider>
    </ReleaseContextProvider>
  );
};

export default ReleaseWrapper;
